// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { translateHref } from 'assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Config
import i18nextConfig from 'next-i18next.config'

// Img
import MonitoringImage from 'public/img/products/Air1000/dathascy_mobile_app.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody } from '@/assets/theme/fonts'

export default function MonitoringSection() {
  const { t } = useTranslation('index')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('monitoring.eyebrow')}
        title={t('monitoring.title')}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 20rem)',
          gap: stacked ? '2rem' : '3rem',
          // Top aligned: the portrait screenshot is far taller than the copy,
          // and centring it pushed the text away from the heading.
          alignItems: 'start'
        }}
      >
        <Box>
          {[t('monitoring.p1'), t('monitoring.p2'), t('monitoring.p3')].map(
            paragraph => (
              <Typography
                key={paragraph}
                component="p"
                fontSize={getFontBody()}
                sx={{
                  lineHeight: 1.75,
                  color: themeColors.gray.defaultText,
                  marginBottom: '1rem',
                  '&:last-of-type': { marginBottom: 0 }
                }}
              >
                {paragraph}
              </Typography>
            )
          )}

          <Link
            href={translateHref(currentLocale, 'iot-air-quality-monitoring')}
            style={{ textDecoration: 'none' }}
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
              sx={{
                marginTop: '1.75rem',
                background: themeColors.success.main,
                borderRadius: '4px',
                padding: '0.7rem 1.4rem',
                fontWeight: 600,
                letterSpacing: '0.03em',
                boxShadow: 'none',
                '&:hover': {
                  background: themeColors.success.darker,
                  boxShadow: 'none'
                }
              }}
            >
              {t('monitoring.link')}
            </Button>
          </Link>
        </Box>

        <Box
          sx={{
            lineHeight: 0,
            borderRadius: '10px',
            overflow: 'hidden',
            order: stacked ? -1 : 0,
            // Lifts the photo into the band the section header occupies, so
            // it starts level with the eyebrow instead of below the title.
            marginTop: stacked ? 0 : '-9rem'
          }}
        >
          <Image
            src={MonitoringImage}
            alt="Air quality readings in the DathaScy mobile app"
            sizes="(max-width: 990px) 100vw, 20rem"
            style={{
              width: '100%',
              height: 'auto',
              maxWidth: MonitoringImage.width
            }}
          />
        </Box>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
